import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  teamsApi,
  type AdminCreateTeamBody,
  type AdminRemoveFromTeamBody,
  type FreezeWarnBody,
  type ListTeamsParams,
  type RemoveFromQueueBody,
  type TerminateTeamBody,
} from '../api/teamsApi';
import { teamsKeys } from '../queryKeys';
import { humanizeError } from '@/lib/apiClient';
import { toast } from '@/hooks/useToast';

export function useTeamsList(params: ListTeamsParams) {
  return useQuery({
    queryKey: teamsKeys.list(params),
    queryFn: () => teamsApi.list(params),
    staleTime: 30_000,
  });
}

export function useTeamDetail(teamId: string | undefined) {
  return useQuery({
    queryKey: teamsKeys.detail(teamId ?? ''),
    queryFn: () => teamsApi.detail(teamId as string),
    enabled: !!teamId,
    staleTime: 30_000,
  });
}

export function useTeamLeaves(teamId: string | undefined) {
  return useQuery({
    queryKey: teamsKeys.leaves(teamId ?? ''),
    queryFn: () => teamsApi.leaves(teamId as string),
    enabled: !!teamId,
    staleTime: 60_000,
  });
}

export function useMatchingQueue(limit = 100) {
  return useQuery({
    queryKey: teamsKeys.queue(limit),
    queryFn: () => teamsApi.queue(limit),
    staleTime: 30_000,
  });
}

function invalidate(qc: ReturnType<typeof useQueryClient>, teamId: string) {
  qc.invalidateQueries({ queryKey: teamsKeys.detail(teamId) });
  qc.invalidateQueries({ queryKey: teamsKeys.lists() });
}

export function useFreezeTeam(teamId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: FreezeWarnBody) => teamsApi.freeze(teamId, body),
    onSuccess: () => {
      invalidate(qc, teamId);
      toast({ title: 'Team frozen', variant: 'success' });
    },
    onError: (err) =>
      toast({ title: 'Freeze failed', description: humanizeError(err), variant: 'destructive' }),
  });
}

export function useWarnTeam(teamId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: FreezeWarnBody) => teamsApi.warn(teamId, body),
    onSuccess: () => {
      invalidate(qc, teamId);
      toast({ title: 'Team warned', variant: 'success' });
    },
    onError: (err) =>
      toast({ title: 'Warn failed', description: humanizeError(err), variant: 'destructive' }),
  });
}

export function useTerminateTeam(teamId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: TerminateTeamBody) => teamsApi.terminate(teamId, body),
    onSuccess: () => {
      invalidate(qc, teamId);
      toast({ title: 'Team terminated', variant: 'success' });
    },
    onError: (err) =>
      toast({ title: 'Terminate failed', description: humanizeError(err), variant: 'destructive' }),
  });
}

export function useAdminRemoveMember(teamId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AdminRemoveFromTeamBody) => teamsApi.adminRemoveMember(teamId, body),
    onSuccess: () => {
      invalidate(qc, teamId);
      toast({ title: 'Member removed', variant: 'success' });
    },
    onError: (err) =>
      toast({ title: 'Remove failed', description: humanizeError(err), variant: 'destructive' }),
  });
}

export function useRemoveFromQueue() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: RemoveFromQueueBody) => teamsApi.removeFromQueue(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: teamsKeys.queue() });
      toast({ title: 'User removed from queue', variant: 'success' });
    },
    onError: (err) =>
      toast({ title: 'Queue remove failed', description: humanizeError(err), variant: 'destructive' }),
  });
}

export function useAdminCreateTeam() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AdminCreateTeamBody) => teamsApi.adminCreateTeam(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: teamsKeys.lists() });
      qc.invalidateQueries({ queryKey: teamsKeys.queue() });
      toast({ title: 'Team created', variant: 'success' });
    },
    onError: (err) =>
      toast({ title: 'Create failed', description: humanizeError(err), variant: 'destructive' }),
  });
}
